import { CircularProgress, type CircularProgressProps, styled } from '@mui/material';
import { Children, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type BaseProps = {
    isLoading?: boolean;
    children?: ReactNode | ReactNode[];
    className?: string;
    size?: CircularProgressProps['size'];
};

type KeepChildrenInDomProps = {
    keepChildrenInDom: true;
    usePortal?: boolean;
    childrenVisible?: boolean;
};
type NoKeepChildrenInDomProps = {
    keepChildrenInDom?: false;
    usePortal?: never;
    childrenVisible?: never;
};
type LoaderProps = BaseProps & (KeepChildrenInDomProps | NoKeepChildrenInDomProps);

const ChildrenWrapper = styled('span')<{ $isLoading: boolean; $childrenVisible: boolean }>(
    ({ $isLoading, $childrenVisible }) => ({
        ...($isLoading && {
            opacity: 0.4,
            visibility: $childrenVisible ? 'visible' : 'hidden',
            pointerEvents: 'none',
            userSelect: 'none',
        }),
    }),
);

const Wrapper = styled('span')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
});

const ParentWrapper = styled('span')({
    display: 'block',
    width: '100%',
    position: 'relative',
    [`${Wrapper}`]: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
    },
});

const Loader = ({
    className,
    keepChildrenInDom = false,
    usePortal,
    isLoading = true,
    children,
    size,
    childrenVisible = false,
}: LoaderProps) => {
    const renderLoader = () => (
        <Wrapper className={className}>
            <CircularProgress size={size} />
        </Wrapper>
    );

    if (keepChildrenInDom) {
        if (usePortal) {
            return isLoading ? (
                <>
                    {renderLoader()}

                    {createPortal(
                        <div
                            style={{
                                position: 'absolute',
                                top: -99999,
                                left: -99999,
                                visibility: 'hidden',
                            }}
                        >
                            {children}
                        </div>,
                        document.body,
                    )}
                </>
            ) : (
                children
            );
        }

        return (
            <ParentWrapper className={className}>
                {isLoading && renderLoader()}
                <ChildrenWrapper $isLoading={isLoading} $childrenVisible={childrenVisible}>
                    {children}
                </ChildrenWrapper>
            </ParentWrapper>
        );
    } else if (isLoading) {
        return renderLoader();
    }

    if (Children.count(children) > 0) {
        return <>{children}</>;
    }

    return null;
};

export default Loader;
